import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import AvisoHomeTour from './AvisoHomeTour';
import AvisoTexto from './AvisoTexto';

// =====================================================================
// SINO DE AVISOS — componente compartilhado
// =====================================================================
// Único dono da lógica do Sino, usado na barra da Home (desktop e mobile) e no
// cabeçalho dos módulos (ModuloHeader). Antes essa casca estava DUPLICADA em 3
// lugares ("Nenhum aviso no momento"); agora há um só componente.
//
// O que faz:
//   - busca os avisos do usuário em GET /api/avisos (lista + se já leu cada um);
//   - mostra um badge com a contagem de NÃO-LIDOS sobre o sino;
//   - abre um dropdown com a lista (aviso lido aparece cinza);
//   - ao clicar num aviso, abre o pop-up de conteúdo daquele aviso e o marca
//     como lido (POST /api/avisos/:chave/ler) — o badge cai na hora.
//
// O CONTEÚDO de cada aviso depende do seu TIPO (campo vindo do GET):
//   - tipo 'modal'  → avisos CURADOS em código; um componente React desenha o
//                     pop-up (o tutorial da Home é o AvisoHomeTour). O mapa
//                     MODAIS_POR_CHAVE liga a chave ao componente. Sem componente
//                     mapeado, só marca como lido (não abre pop-up).
//   - tipo 'texto'  → avisos CADASTRADOS no Admin (dtbAvisos); o pop-up genérico
//                     AvisoTexto desenha o corpo/link que vêm no próprio aviso.
//
// Props (para encaixar nas 3 barras, que têm tamanhos/cores diferentes):
//   - cor          : cor do ícone do sino (amarelo na Home, cor do módulo no header)
//   - tamanhoIcone : tamanho do Bell (24 desktop, 22 mobile)
//   - larguraLista : largura do dropdown (px)
//   - topoLista    : distância do topo do dropdown em relação ao botão (px)
// ---------------------------------------------------------------------

// Liga a chave do aviso ao componente que desenha seu conteúdo.
const MODAIS_POR_CHAVE = {
    'home-tour-2026-06': AvisoHomeTour,
};

const FONTE = "'Raleway', sans-serif";
const CARTAO = {
    backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
};

function SinoAvisos({ cor = '#FDBB20', tamanhoIcone = 24, larguraLista = 280, topoLista = 46 }) {
    const [avisos, setAvisos] = useState([]);
    const [listaAberta, setListaAberta] = useState(false);
    const [avisoAberto, setAvisoAberto] = useState(null);   // aviso com o pop-up aberto (payload)
    const containerRef = useRef(null);

    const token = localStorage.getItem('authToken');

    // Carrega os avisos do usuário ao montar. Falha silenciosa: o Sino é
    // acessório — se a API cair, ele só não mostra avisos (não quebra a barra).
    const carregar = useCallback(async () => {
        try {
            const r = await fetch('/api/avisos', { headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) setAvisos(await r.json());
        } catch (e) {
            /* Sino é acessório — ignora erro de rede. */
        }
    }, [token]);

    useEffect(() => { carregar(); }, [carregar]);

    // Fecha o dropdown ao clicar fora dele.
    useEffect(() => {
        if (!listaAberta) return;
        const aoClicarFora = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setListaAberta(false);
            }
        };
        document.addEventListener('mousedown', aoClicarFora);
        return () => document.removeEventListener('mousedown', aoClicarFora);
    }, [listaAberta]);

    const naoLidos = avisos.filter((a) => !a.lido).length;

    // Clique num aviso da lista: abre o pop-up (se houver) e marca como lido.
    const abrirAviso = async (aviso) => {
        setListaAberta(false);
        // Abre o pop-up conforme o tipo: 'texto' (banco) usa o AvisoTexto genérico;
        // qualquer outro (ex.: 'modal') só abre se houver componente mapeado.
        if (aviso.tipo === 'texto' || MODAIS_POR_CHAVE[aviso.chave]) setAvisoAberto(aviso);

        if (aviso.lido) return;   // já lido: nada a gravar
        // Otimista: pinta como lido na hora (badge cai); confirma no servidor.
        setAvisos((prev) => prev.map((a) => (a.chave === aviso.chave ? { ...a, lido: true } : a)));
        try {
            await fetch(`/api/avisos/${aviso.chave}/ler`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (e) {
            /* Se falhar, recarrega para refletir o estado real do servidor. */
            carregar();
        }
    };

    // Para avisos em código, resolve o componente pela chave; 'texto' usa AvisoTexto.
    const ModalCodigo = avisoAberto && avisoAberto.tipo !== 'texto'
        ? MODAIS_POR_CHAVE[avisoAberto.chave]
        : null;

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setListaAberta((v) => !v)}
                title="Avisos"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: cor, display: 'flex', alignItems: 'center', padding: '6px', position: 'relative' }}
            >
                <Bell size={tamanhoIcone} />
                {/* Badge de não-lidos — só aparece quando há aviso por ler */}
                {naoLidos > 0 && (
                    <span style={{
                        position: 'absolute', top: '-2px', right: '-2px',
                        minWidth: '18px', height: '18px', padding: '0 4px', boxSizing: 'border-box',
                        backgroundColor: '#990605', color: '#fff', borderRadius: '999px',
                        fontSize: '11px', fontWeight: 700, lineHeight: '18px', textAlign: 'center',
                        fontFamily: FONTE,
                    }}>
                        {naoLidos}
                    </span>
                )}
            </button>

            {listaAberta && (
                <div style={{ position: 'absolute', right: 0, top: `${topoLista}px`, width: `${larguraLista}px`, ...CARTAO, padding: '8px 0', zIndex: 40, fontFamily: FONTE }}>
                    <div style={{ fontWeight: 700, color: '#1a1a1a', padding: '8px 16px 10px', borderBottom: '1px solid rgba(199,194,194,0.45)' }}>
                        Avisos
                    </div>

                    {avisos.length === 0 ? (
                        <div style={{ color: '#7a7a7a', fontSize: '13px', padding: '14px 16px' }}>
                            Nenhum aviso no momento.
                        </div>
                    ) : (
                        avisos.map((a) => (
                            <button
                                key={a.chave}
                                onClick={() => abrirAviso(a)}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                                    background: 'none', border: 'none', padding: '10px 16px', fontFamily: FONTE,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f7f6f6'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {/* Bolinha de não-lido (some quando o aviso vira cinza) */}
                                    {!a.lido && <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#990605', flexShrink: 0 }} />}
                                    <span style={{
                                        fontSize: '14px', fontWeight: a.lido ? 500 : 700,
                                        color: a.lido ? '#9a9a9a' : '#1a1a1a',
                                    }}>
                                        {a.titulo}
                                    </span>
                                </div>
                                <div style={{ fontSize: '12px', color: a.lido ? '#b0b0b0' : '#7a7a7a', marginTop: '2px', paddingLeft: a.lido ? 0 : '16px' }}>
                                    {a.resumo}
                                </div>
                            </button>
                        ))
                    )}
                </div>
            )}

            {/* Pop-up do aviso aberto: componente em código (ex.: tutorial da Home)... */}
            {ModalCodigo && <ModalCodigo onClose={() => setAvisoAberto(null)} />}
            {/* ...ou o pop-up genérico de texto (avisos cadastrados no Admin). */}
            {avisoAberto?.tipo === 'texto' && (
                <AvisoTexto
                    titulo={avisoAberto.titulo}
                    data={avisoAberto.data}
                    corpo={avisoAberto.corpo}
                    imagemUrl={avisoAberto.imagemUrl}
                    linkUrl={avisoAberto.linkUrl}
                    linkTexto={avisoAberto.linkTexto}
                    onClose={() => setAvisoAberto(null)}
                />
            )}
        </div>
    );
}

export default SinoAvisos;
